---
title: Validační konzole
sidebar_position: 4
description: Validační konzole
---

# Validační konzole

**Validační konzole** umožňuje uživatelům organizovat, spravovat a dokončovat úkoly odborné revize pomocí balíčků sad (bundle packages) pro účely validace a trénování.

## Přehled

**Účel**:

- Shromažďovat pozorování z terénních návštěv
- Validovat predikce ze satelitních dat
- Poskytovat trénovací data pro algoritmy indikátorů
- Podporovat ověřování shody
- Organizovat úkoly odborné revize do strukturovaných balíčků
- Sledovat průběh úkolů prostřednictvím stavových fází
- Umožňovat systematickou terénní validaci a revizi shody

**Klíčové komponenty**:

- **Sady (Bundles)**: Pojmenované skupiny balíčků úkolů sestavené z filtrů zákresů JŽ a vstupních definic
- **Balíčky (Packages)**: Jednotlivá přiřazení úkolů pro odborníky; každý balíček pokrývá podmnožinu zákresů JŽ dané sady
- **Pohled validace**: Rozhraní pro dokončování přiřazených revizí
- **Sestavy** (závislé na rozsahu): Generování PDF sestavy pro vybraná pozorování

**Sledování stavu** — Balíčky procházejí těmito fázemi:

1. **Nepřiřazeno (Unassigned)**: Dostupný k přiřazení
2. **Zpracovává se (Processing)**: Přidělen odborníkovi, probíhá práce
3. **Dokončeno (Finished)**: Revize dokončena (hodnota stavu v API je `COMPLETED`; to je důležité při filtrování balíčků podle stavu)

**Oprávnění**:
Vytvoření sady vyžaduje roli **GTC-ADMIN**. Zobrazení sad, balíčků a pohled validace vyžadují minimálně přístup **GTC-VIEWER** (pouze čtení/zobrazení) nebo **GTC-OPERATOR** (zápis — poskytování uživatelských vstupů).

## Zobrazení pro vytvoření sady

**Přístup**: Přejděte přes sbalitelné menu bočního panelu — **Vytvoření sady**. Vyžaduje roli **GTC-ADMIN**.

![Rozhraní modulu Vytvoření sady](../imgs/bundle-create.png)

Sada definuje, které zákresy JŽ vstoupí do balíčků a jaké vstupy musí odborníci vyplnit pro každý zákres JŽ. Tlačítko **Vytvořit** je neaktivní, dokud nebude přidán alespoň jeden filtr.

### Krok 1 — Nastavte název sady a velikost balíčku

1. Zadejte **Název sady** pro pozdější identifikaci.
2. Nastavte **Velikost balíčku** — maximální počet zákresů JŽ v jednom balíčku. Systém vytvoří `ceil(počet zákresů JŽ / velikost balíčku)` balíčků.

**Pevné limity: max. 1 000 zákresů JŽ na balíček a max. 1 000 balíčků na sadu.** Pokud by některá hodnota přesáhla 1 000, sada okamžitě selže se stavem **FAILED** — žádné balíčky nebudou vytvořeny. Například nastavení velikosti balíčku na 2 000 pro 3 000 nalezených zákresů JŽ by vytvořilo 2 balíčky po 2 000, oba přes limit na balíček, a sada selže. Postup řešení viz [Stav sady: FAILED](07-support-troubleshooting.mdx#problém-stav-vytvoření-sady-je-failed).

### Krok 2 — Definujte vstupní definice

Vstupní definice určují, která formulářová pole se zobrazí v pohledu validace pro každý zákres JŽ, aby uživatelé mohli zadat své vstupy. Přidejte tolik vstupů, kolik potřebujete. Pokud je potřeba pouze revize bez uživatelského vstupu, tento krok přeskočte a vytvořte sadu bez vstupů — to je užitečné, pokud potřebujete kolekci zákresů JŽ na základě dotazů (převážně na indikátory) nebo pro generování PDF sestav bez nutnosti odborného vstupu.

1. Vyberte typ vstupu:

| Typ             | Pole                                                                                                                                                                                                                                                  |
| --------------- | ----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------- |
| **Poznámka**    | **Popisek** (povinný);<br />zaškrtávací políčko **Povinné**                                                                                                                                                                                           |
| **Klasifikace** | **Popisek** (povinný);<br />dvě nebo více **možností**, každá s popiskem a **klávesovou zkratkou** (1 znak);<br />příznak **Vícenásobný výběr**, umožňující uživatelům vybrat více než jednu možnost ze seznamu;<br />zaškrtávací políčko **Povinné** |
| **Pozorování**  | **Popisek** (povinný);<br />**klávesová zkratka** (1 znak);<br />**zkratka** (1–3 znaky);<br />`#RRGGBB` **barva**                                                                                                                                    |
| **Událost**     | **Popisek** (povinný);<br />**klávesová zkratka** (1 znak);<br />**zkratka** (1–3 znaky);<br />`#RRGGBB` **barva**                                                                                                                                    |

2. Vyplňte pole pro vybraný typ.
3. Klikněte na **Přidat** a přidejte vstupní definici do sady.
4. Opakujte pro každý další vstup.

### Krok 3 — Nastavte filtry sady

Filtry určují, které zákresy JŽ budou zahrnuty. Všechny filtry jsou kombinovány operátorem **AND**. Před vytvořením sady je vyžadován alespoň jeden filtr.

#### Seznam zákresů JŽ

1. Zadejte ID zákresů JŽ jako seznam oddělený čárkami nebo mezerami. Každé ID musí být ve formátu `<SCOPE>.FOI.<ID>`.
2. Klikněte na **Přidat filtr**.

Příklad:

```
DEMO_FR23.FOI.92289506, DEMO_FR23.FOI.92289498, DEMO_FR23.FOI.92289496, DEMO_FR23.FOI.92289493, DEMO_FR23.FOI.92289485, DEMO_FR23.FOI.92289473, DEMO_FR23.FOI.92289471, DEMO_FR23.FOI.92289466, DEMO_FR23.FOI.92289457, DEMO_FR23.FOI.92289420
```

#### Dotaz na zákresy JŽ

1. Vyberte **Datovou sadu zákresů JŽ**. Datová sada zákresů JŽ je exportovaná referenční data, která byla použita pro výpočet signálů a indikátorů. Každá datová sada:
   - pokrývá konkrétní časové okno, a
   - obsahuje _snímek_ zákresů JŽ a jejich atributů v době exportu.

   Protože se jedná o snímek, filtrování podle atributu se vztahuje na hodnoty _zachycené v této datové sadě_, ne nutně na aktuální hodnoty v systému. Pro rozsahy s jediným importem referenčních dat nezáleží na výběru. Pro rozsahy s více importy (a tedy více datovými sadami zákresů JŽ) vyberte tu datovou sadu, jejíž časové okno nejlépe odpovídá vašim filtrovacím kritériím — obvykle tu nejnovější.

2. Vyberte **atribut** pro filtrování. Dostupné atributy zahrnují všechny standardní atributy zákresů JŽ: `id`, `ref_id`, `parent_id`, `foi_type`, `holding_id`, `crop_id`, `land_use_id`, `geometry`, `elevation`, `slope`, `orientation`, `area`, `perimeter`, `centroid`, `soil_type_id`, `agricultural_practice_id` a vlastní dodatečné atributy datové sady.

3. Vyberte **operaci**: `EQUALS`, `NOT_EQUALS`, `LESS_THAN`, `GREATER_THAN` nebo `IN` (více hodnot).

4. Zadejte **hodnotu** (nebo více hodnot pro `IN`). Typ vstupu závisí na typu atributu:
   - Pro řetězcové atributy jde o textový vstup přesné hodnoty řetězce.
   - Pro numerické atributy jde o číselný vstup (viz odstavec níže pro více informací).
   - Pro geometrii jde o řetězec WKT (např. `POLYGON((...))`).
   - Pro číselníky jde o výběr z prohledávatelného rozbalovacího seznamu položek číselníku (viz odstavec níže pro více informací).

5. Klikněte na **Přidat filtr**.

**Jednotky pro atributy měření** (zadávejte prosté číselné hodnoty v těchto jednotkách):

| Atribut       | Jednotka                                                                   |
| ------------- | -------------------------------------------------------------------------- |
| `area`        | čtvereční metry (m²) — např. 2 ha parcela ≈ `20000`                        |
| `perimeter`   | metry (m)                                                                  |
| `elevation`   | metry (m), průměr zákresu JŽ                                               |
| `slope`       | procenta (%), průměr zákresu JŽ                                            |
| `orientation` | stupně (°), azimut — 0° = sever, roste po směru hodinových ručiček (0–360) |

**Filtrování atributů číselníku podle kódu:**
Vyberete-li atribut napojený na číselník (`foi_type`, `crop_id`, `land_use_id`, `soil_type_id` nebo `agricultural_practice_id`), pole **hodnota** se změní na prohledávatelný **rozbalovací seznam** položek tohoto číselníku zobrazených s jejich čitelnými popisky (není již nutné zadávat plně kvalifikované ID jako `DEMO_FR23.CL_CROP.VRC`). Psaním filtrujte seznam buď podle popisku, nebo krátkého kódu (např. `VRC`), a pro operaci `IN` můžete vybrat více hodnot. Zadáním známého krátkého kódu se automaticky vybere správná položka.

#### Dotaz na indikátory

1. Vyberte **kontext indikátorů** (viz [Klíčové pojmy](01-introduction.mdx#kontext-indikátorů)). Kontext určuje, které výpočty indikátorů se použijí pro filtrování.

2. Vyberte **typ indikátoru** (systém automaticky vyřeší příslušnou inferenci).

3. Vyberte **atribut** indikátoru.

4. Vyberte **operaci**: `EQUALS`, `NOT_EQUALS`, `LESS_THAN`, `GREATER_THAN` nebo `IN` (více hodnot).

5. Zadejte **hodnotu**:
   - Pro řetězcové atributy jde o textový vstup přesné hodnoty řetězce.
   - Pro numerické atributy jde o číselný vstup (viz odstavec níže pro více informací).

6. Klikněte na **Přidat filtr**.

**Jak se vyhodnocují filtry atributů indikátorů?** Filtr se spouští proti inferenční datové sadě, která odpovídá vybranému **kontextu indikátorů** a **typu indikátoru**. Tato datová sada pokrývá konkrétní časové okno — stejný kontext a časový rozsah zobrazený na záložce **Souhrn indikátorů** v pohledu zákresu JŽ nebo pohledu validace, pokud je daný kontext aktivní. To znamená, že počet záznamů odpovídajících filtru je stejný jako počet viditelný v souhrnu indikátorů **pro daný kontext**. Pokud byl filtr vytvořen s jiným kontextem indikátorů, než který je vybrán v nastavení rozsahu, výsledky indikátorů zobrazené na záložce Souhrn indikátorů v pohledu zákresu JŽ nebo pohledu validace nemusí nutně odpovídat výsledkům filtru.

Příklad: pokud filtrujete atribut počtu pozorování indikátoru holé půdy na hodnotu `2`, sada zahrne pouze zákresy JŽ, které mají přesně dvě detekce holé půdy v časovém okně inference pro vybraný kontext — odpovídající tomu, co byste viděli v **souhrnu indikátorů pohledu zákresu JŽ** pro daný kontext.

##### Atributy indikátorů podle typu indikátoru

Atributy zobrazené v rozbalovacím seznamu **atribut** závisí na **rodině (skupině) typu indikátoru**. Zobrazené názvy jsou čitelné popisky — služba indikátorů mapuje každý výstupní sloupec indikátoru na popisek, takže nepracujete přímo s názvy sloupců. Tabulky níže uvádějí pro každou rodinu atributy, na které typicky filtrujete, jejich význam a hodnoty, které obsahují.

Každý typ indikátoru také zpřístupňuje tři systémová pole — **ID zákresu JŽ**, **ID indikátoru** a **Stav** (`0` = OK, `1` = chyba při spuštění, `2` = nespuštěno, `3` = spuštěno, ale nic nedetekováno, u indikátorů událostí). Obvykle filtrujete na níže uvedené atributy specifické pro indikátory.

**Indikátory agregované hodnoty** (např. průměrný NDVI)

| Atribut            | Představuje                                                | Hodnoty |
| ------------------ | ---------------------------------------------------------- | ------- |
| Hodnota indikátoru | Agregovaná hodnota signálu za období (např. průměrný NDVI) | číslo   |

**Holá půda**

| Atribut                              | Představuje                                         | Hodnoty    |
| ------------------------------------ | --------------------------------------------------- | ---------- |
| Počet pozorování                     | Celkový počet pozorování holé půdy v období         | celé číslo |
| Pozorování indikátorů — spolehlivost | Pravděpodobnost holé půdy pro jednotlivá pozorování | 0–1        |
| Pozorování indikátorů — datum        | Datum pozorování holé půdy                          | datum      |

**Indikátory počtu** (např. počet událostí sekání)

| Atribut | Představuje                            | Hodnoty    |
| ------- | -------------------------------------- | ---------- |
| Počet   | Počet detekovaných událostí/pozorování | celé číslo |

**Klasifikační indikátory** — Skupina plodin, Skupina půdního pokryvu, Klasifikace plodin, Podobnost, Vzdálenost

| Atribut                                                 | Představuje                                                          | Hodnoty    |
| ------------------------------------------------------- | -------------------------------------------------------------------- | ---------- |
| Klasifikováno jako                                      | Predikovaný / nejpodobnější kód třídy                                | kód třídy  |
| Deklarováno jako                                        | Deklarovaný kód třídy (není k dispozici pro skupinu půdního pokryvu) | kód třídy  |
| Skóre klasifikace                                       | Spolehlivost predikované třídy                                       | 0–100      |
| Skóre indikátorů — hypotéza                             | Kandidátní třída s přiřazeným skóre                                  | kód třídy  |
| Skóre indikátorů                                        | Skóre dané hypotézové třídy                                          | 0–100      |
| Počet zákresů JŽ se stejnou plodinou (pouze Vzdálenost) | Sousedé se stejnou deklarovanou plodinou použití pro porovnání       | celé číslo |

**Homogenita**

| Atribut                                        | Představuje                         | Hodnoty                         |
| ---------------------------------------------- | ----------------------------------- | ------------------------------- |
| Klasifikováno jako                             | Výsledek na úrovni zákresu JŽ       | `homogeneous` / `heterogeneous` |
| Skóre klasifikace                              | Spolehlivost výsledku               | 0–100                           |
| Skóre indikátorů — hypotéza / Skóre indikátorů | Popisek a skóre pro každou hypotézu | popisek / 0–100                 |

**Rozhodnutí (semafor)**

| Atribut                       | Představuje                                       | Hodnoty |
| ----------------------------- | ------------------------------------------------- | ------- |
| Kód rozhodnutí                | Kód výsledku rozhodnutí                           | kód     |
| Kód barvy semaforu rozhodnutí | Přiřazená barva (např. zelená/žlutá/červená)      | kód     |
| Kód pruhu rozhodnutí          | Který pruh/cesta rozhodnutí výsledek vyprodukoval | kód     |

**Hodnocení**

| Atribut   | Představuje                           | Hodnoty      |
| --------- | ------------------------------------- | ------------ |
| Hodnocení | Zda zákres JŽ splňuje výraz hodnocení | true / false |

**Indikátory událostí** — Sekání, Orba a podobné

| Atribut                                        | Představuje                                        | Hodnoty    |
| ---------------------------------------------- | -------------------------------------------------- | ---------- |
| Datum začátku události / Datum konce události  | Kdy detekovaná událost začíná / končí              | datum      |
| Délka indikátoru                               | Délka události                                     | dny        |
| Počet pozorování indikátoru                    | Platná pozorování v okně události                  | celé číslo |
| Počáteční NDVI / Koncový NDVI / Minimální NDVI | NDVI na začátku, konci a v nejnižším bodě události | číslo      |
| Pokles NDVI                                    | Velikost poklesu NDVI                              | číslo      |

Generické indikátory událostí navíc zpřístupňují podrobné časové razítky a hodnoty pro jednotlivé body (časová razítka události/prahu/extrémů a jejich hodnoty FEATURE a pravděpodobnost události) pro jemnozrnnější dotazy.

**Sloučené sekání**

| Atribut                              | Představuje                                       | Hodnoty           |
| ------------------------------------ | ------------------------------------------------- | ----------------- |
| Datum začátku / Datum konce události | Okno sloučené události                            | datum             |
| Pravděpodobnost události             | Spolehlivost sloučené události                    | číslo             |
| Počet vstupů / Vstupy                | Kolik zdrojových indikátorů bylo sloučeno a které | celé číslo / text |

**Zelenání a sklizeň**

| Atribut                                                    | Představuje                               | Hodnoty |
| ---------------------------------------------------------- | ----------------------------------------- | ------- |
| Datum začátku/konce zelenání; Datum začátku/konce sklizně  | Okna událostí zelenání a sklizně          | datum   |
| Délka zelenání / Délka sklizně                             | Délka každé fáze                          | dny     |
| Počáteční NDVI, koncový NDVI, pokles NDVI zelenání/sklizně | NDVI na hranicích fáze a velikost poklesu | číslo   |

**Pixelové sekání**

| Atribut                                                          | Představuje                                        | Hodnoty          |
| ---------------------------------------------------------------- | -------------------------------------------------- | ---------------- |
| Detekováno sekání                                                | Zda bylo detekováno nějaké sekání                  | true / false     |
| Počet pixelů s detekovaným sekáním                               | Absolutní počet posečených pixelů                  | celé číslo       |
| Podíl pixelů s detekovaným sekáním                               | Posečené pixely jako zlomek zákresu JŽ             | 0–1              |
| Max. počet / Max. podíl propojených pixelů s detekovaným sekáním | Největší souvislá posečená plocha (počet / zlomek) | celé číslo / 0–1 |
| Počet všech pixelů zákresu JŽ                                    | Celkový počet pixelů zákresu JŽ                    | celé číslo       |

**Čištění pólů**

| Atribut                              | Představuje                                  | Hodnoty    |
| ------------------------------------ | -------------------------------------------- | ---------- |
| Počet pozorování                     | Celkový počet detekcí čištění                | celé číslo |
| Pozorování indikátorů — datum        | Predikované datum čištění                    | datum      |
| Pozorování indikátorů — spolehlivost | Pravděpodobnost, že je oblast pólu vyčištěna | 0–1        |

### Krok 4 — Vytvořte sadu

1. Zkontrolujte veškerá nastavení.
2. Klikněte na **Vytvořit**.

Sada je vytvořena se stavem **CREATED**, přejde do stavu **PROCESSING** během generování balíčků a nakonec dosáhne stavu **DONE** (nebo **FAILED** v případě chyby). Balíčky jsou dostupné ihned po dosažení stavu **DONE**.

## Zobrazení sad

**Přístup**: Přejděte přes sbalitelné menu bočního panelu — **Sady**.

![Modul Sady zobrazující seznam sad](../imgs/bundle-list.png)

Zobrazení sad uvádí všechny sady v rozsahu. Sloupce zahrnují název sady, datum vytvoření, počet balíčků a stav sady. Seznam lze filtrovat a řadit.

**Akce pro každou sadu**:

- **Upravit název**: Přejmenování sady.
- **Otevřít sadu**: Přechod do zobrazení balíčků filtrovaného na tuto sadu.
- **Smazat**: Smazání sady, jejích balíčků a všech přidružených dat. Před smazáním se zobrazí potvrzovací dialog.

## Zobrazení balíčků

**Přístup**: Přejděte přes sbalitelné menu bočního panelu — **Balíčky**.

![Rozhraní modulu Balíčky](../imgs/packages-view.png)

Zobrazení balíčků uvádí všechny balíčky napříč všemi sadami. Filtrujte podle stavu, přiřazeného uživatele, ID sady nebo ID balíčku.

**Výběr balíčků**: Kliknutím na řádek jej vyberete. Podržte **CTRL** (nebo **CMD** na Macu) pro přidání nebo odebrání jednotlivých řádků — výběry provedené tímto způsobem přetrvávají napříč stránkami. Podržte **SHIFT** pro výběr souvislého rozsahu; výběr rozsahem SHIFT funguje pouze v rámci aktuální stránky (kotva se resetuje při změně stránky).

**Akce dostupné z panelu nástrojů**:

- **Přiřadit / Přeřadit**: Přiřazení nepřiřazených balíčků nebo přeřazení již přiřazených balíčků vybranému uživateli.
- **Exportovat do CSV**: Export dat balíčků do souboru CSV (jeden soubor `.csv` na vybraný balíček).
- **Smazat**: Smazání balíčku a všech jeho shromážděných dat (uživatelských vstupů). Před smazáním potvrďte v dialogu.

**Akce pro každý řádek** (sloupec Akce):

- **Otevřít balíček** (ikona zaměření): Otevření balíčku přímo v pohledu validace.

## Pohled validace

**Přístup**: Přejděte přes sbalitelné menu bočního panelu — **Pohled validace**, nebo otevřete balíček přímo ze zobrazení balíčků.

**Poznámka**: Pohled validace sdílí komponenty s modulem pohledu zákresu JŽ (viz [Modul Pohledu zákresu JŽ](03-foi-view-module.mdx)) — widget pozorování, widget mapy a vizualizaci signálů/indikátorů. Tato sekce pokrývá pracovní postup specifický pro validaci.

![Pohled validace](../imgs/validation-view.png)

Záhlaví aplikace zobrazuje aktuální rozsah, **ID sady** a **ID balíčku** v době práce uvnitř balíčku. Kliknutím na **ID sady** se otevře rozbalovací nabídka se dvěma možnostmi: **"Zobrazit sadu v pohledu Sady"** (přejde do zobrazení sad filtrovaného na tuto sadu) a **"Zobrazit balíčky sady v pohledu Balíčky"** (přejde do zobrazení balíčků filtrovaného na balíčky dané sady). Kliknutím na **ID balíčku** se otevře rozbalovací nabídka s jednou možností: **"Zobrazit balíček v pohledu Balíčky"** (přejde do zobrazení balíčků filtrovaného na tento konkrétní balíček).

### Výběr zákresu JŽ

1. Po otevření balíčku zobrazí levý sloupec seznam zákresů JŽ v daném balíčku.
2. Kliknutím na zákres JŽ v seznamu jej načtete do rozhraní revize.

### Prohlížení dat

Pro vybraný zákres JŽ zkontrolujte:

- **Pozorování**: Satelitní snímky v časové ose
- **Indikátory**: Predikce a hodnoty spolehlivosti
- **Signály**: Časové řady signálů v grafu
- **Mapa**: Prostorový kontext a sousední zákresy JŽ

### Typy vstupních formulářů

Pod seznamem zákresů JŽ zobrazuje levý sloupec vstupní formuláře definované pro tuto sadu.

#### Vstupy poznámky

1. Zadejte poznámku do textového pole.
2. Klikněte na **Uložit**.

#### Vstupy klasifikace

1. Vyberte jednu možnost z rozbalovacího seznamu klasifikace (nebo více možností, pokud má vstup povolen **Vícenásobný výběr**, případně použijte klávesovou zkratku definovanou pro každou možnost).
2. Klikněte na **Uložit**.

#### Vstupy pozorování

1. Aktivujte vstup kliknutím na něj ve formuláři nebo stisknutím jeho klávesové zkratky.
2. Ve widgetu pozorování vyberte jedno nebo více dat pro pozorování.
3. Klikněte na **Uložit**.

#### Vstupy události

1. Aktivujte vstup kliknutím na něj ve formuláři nebo stisknutím jeho klávesové zkratky.
2. Ve widgetu pozorování vyberte datum začátku a konce události.
3. Klikněte na **Uložit**.

### Ukládání a odesílání dat

| Akce          | Výsledek                                                       |
| ------------- | -------------------------------------------------------------- |
| **Uložit**    | Uloží zadaná data; zákres JŽ zůstane ve stavu _zpracovává se_  |
| **Hotovo**    | Uloží data a označí zákres JŽ jako _dokončený_                 |
| **Přeskočit** | Označí zákres JŽ jako _přeskočený_ a přejde na další zákres JŽ |

### Dokončení balíčku

1. Po přezkoumání všech zákresů JŽ:
2. Klikněte na **Dokončit balíček**.
3. Potvrďte dokončení v dialogu.
4. Stav balíčku se změní na **Dokončeno** (hodnota API: `COMPLETED`) a balíček je uzavřen.

Před dokončením musí být všechny zákresy JŽ přezkoumány (dokončeny nebo přeskočeny). Chybějící rozhodnutí budou označena.

## PDF sestava

![Záložka Sestavy v pohledu validace zobrazující výzvu k výběru pozorování, pole Identifikátor vyhodnocení a Volné textové pole a tlačítko Vygenerovat PDF](../imgs/validation-console-reports-tab.png)

Záložka **Sestavy** se v levém sloupci pohledu validace zobrazí pouze tehdy, pokud má rozsah povoleno generování PDF sestav (`reporting.pdfGeneration.show = true` v konfiguraci rozsahu). Pokud záložka není viditelná, tento rozsah danou funkci nemá aktivní.

### Generování sestavy

1. V pohledu validace klikněte na záložku **Sestavy** v levém sloupci.
2. Vyberte **1–6 pozorování** ve widgetu pozorování. Při výběru každého z nich se na dlaždici pozorování zobrazí modrá ikona PDF a na grafu signálů/indikátorů se zobrazí modrý svislý pruh. Čítač **N/6** na záložce Sestavy se aktualizuje s každým výběrem.
3. Vyplňte pole **Identifikátor vyhodnocení** (povinné).
4. Volitelně vyplňte pole **Volné textové pole**.
5. Klikněte na **Vygenerovat PDF**. Tlačítko je aktivní pouze tehdy, když je identifikátor neprázdný, je vybráno 1–6 pozorování a žádné generování neprobíhá. PDF se po dokončení automaticky stáhne.
